/**
 * CMS Site Zustand store following ADR-021 sliced store with immer middleware.
 *
 * State:  { sites, selectedSiteId, isLoading, error }
 * Actions: { fetchSites, createSite, updateSite, deleteSite, selectSite, publishSite }
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { CmsSite, SiteSection, CmsTemplate } from '../../types/cms.types';
import { cmsSitesApi, templatesApi } from '../../services/cms.api';
import { logger } from '../../services/logger.client';

const log = logger.child({ module: 'siteStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface SiteState {
  sites: CmsSite[];
  selectedSiteId: string | null;
  sections: SiteSection[];
  templates: CmsTemplate[];
  isLoading: boolean;
  isPublishing: boolean;
  error: string | null;
  /** Set to true after the first fetchSites completes (success or error). */
  hasFetched: boolean;
}

interface SiteActions {
  fetchSites: () => Promise<void>;
  createSite: (data: Partial<CmsSite>) => Promise<CmsSite | null>;
  updateSite: (id: string, data: Partial<CmsSite>) => Promise<void>;
  deleteSite: (id: string) => Promise<void>;
  selectSite: (id: string | null) => void;
  fetchSections: (siteId: string) => Promise<void>;
  updateSections: (siteId: string, sections: Partial<SiteSection>[]) => Promise<void>;
  fetchTemplates: () => Promise<void>;
  publishSite: (id: string) => Promise<void>;
  clearError: () => void;
}

export type SiteStore = SiteState & { actions: SiteActions };

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useSiteStore = create<SiteStore>()(
  immer((set) => ({
    /* ---------- initial state ---------- */
    sites: [],
    selectedSiteId: null,
    sections: [],
    templates: [],
    isLoading: false,
    isPublishing: false,
    error: null,
    hasFetched: false,

    /* ---------- actions ---------- */
    actions: {
      fetchSites: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Fetching sites');
          const sites = await cmsSitesApi.list();

          set((state) => {
            state.sites = sites;
            state.isLoading = false;
            state.hasFetched = true;
          });

          log.info('Sites fetched', { count: sites.length });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to fetch sites';
          log.error('Failed to fetch sites', { error: message });

          set((state) => {
            state.isLoading = false;
            state.hasFetched = true;
            state.error = message;
          });
        }
      },

      createSite: async (data: Partial<CmsSite>) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Creating site', { name: data.name });
          const site = await cmsSitesApi.create(data);

          set((state) => {
            state.sites.push(site);
            state.selectedSiteId = site.id;
            state.isLoading = false;
          });

          log.info('Site created', { id: site.id });
          return site;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to create site';
          log.error('Failed to create site', { error: message });

          set((state) => {
            state.isLoading = false;
            state.error = message;
          });
          return null;
        }
      },

      updateSite: async (id: string, data: Partial<CmsSite>) => {
        set((state) => {
          state.error = null;
        });

        try {
          log.info('Updating site', { id });
          const updated = await cmsSitesApi.update(id, data);

          set((state) => {
            const idx = state.sites.findIndex((s) => s.id === id);
            if (idx !== -1) {
              state.sites[idx] = updated;
            }
          });

          log.info('Site updated', { id });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to update site';
          log.error('Failed to update site', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      deleteSite: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          log.info('Deleting site', { id });
          await cmsSitesApi.delete(id);

          set((state) => {
            state.sites = state.sites.filter((s) => s.id !== id);
            if (state.selectedSiteId === id) {
              state.selectedSiteId = null;
            }
          });

          log.info('Site deleted', { id });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to delete site';
          log.error('Failed to delete site', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      selectSite: (id: string | null) => {
        set((state) => {
          state.selectedSiteId = id;
        });
      },

      fetchSections: async (siteId: string) => {
        try {
          log.info('Fetching sections', { siteId });
          const sections = await cmsSitesApi.getSections(siteId);

          set((state) => {
            state.sections = sections;
          });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to fetch sections';
          log.error('Failed to fetch sections', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      updateSections: async (siteId: string, sections: Partial<SiteSection>[]) => {
        try {
          log.info('Updating sections', { siteId });
          const updated = await cmsSitesApi.updateSections(siteId, sections);

          set((state) => {
            state.sections = updated;
          });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to update sections';
          log.error('Failed to update sections', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      fetchTemplates: async () => {
        try {
          log.info('Fetching templates');
          /* Clear any stale error so the template panel renders its children.
             Without this, a previous operation's error would hide the panel. */
          set((state) => { state.error = null; });
          const templates = await templatesApi.list();

          set((state) => {
            state.templates = templates;
          });

          log.info('Templates fetched', { count: templates.length });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to fetch templates';
          log.error('Failed to fetch templates', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      publishSite: async (id: string) => {
        set((state) => {
          state.isPublishing = true;
          state.error = null;
        });

        try {
          log.info('Publishing site', { id });
          await cmsSitesApi.publish(id);

          set((state) => {
            const idx = state.sites.findIndex((s) => s.id === id);
            if (idx !== -1) {
              state.sites[idx]!.is_dirty = false;
              state.sites[idx]!.last_published_at = new Date().toISOString();
            }
            state.isPublishing = false;
          });

          log.info('Site published', { id });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to publish site';
          log.error('Failed to publish site', { error: message });

          set((state) => {
            state.isPublishing = false;
            state.error = message;
          });
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    },
  })),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks (ADR-021 wrapper-hooks pattern)                    */
/* ------------------------------------------------------------------ */

/** Stable reference to actions -- never causes a re-render. */
export function useSiteActions() {
  return useSiteStore((s) => s.actions);
}

/** Get the currently selected site. */
export function useSelectedSite(): CmsSite | null {
  const sites = useSiteStore((s) => s.sites);
  const selectedId = useSiteStore((s) => s.selectedSiteId);

  return sites.find((s) => s.id === selectedId) ?? null;
}
